import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  TrendingUp,
  TrendingDown,
  Users,
  Eye,
  MousePointer,
  Calendar,
  BarChart3,
  Activity,
  Target,
  Award,
  RefreshCw,
  MessageSquare
} from 'lucide-react';
import { useQuery } from "@tanstack/react-query";
import {
  analyticsApi,
  projectsApi,
  servicesApi,
  clientsApi,
  testimonialsApi
} from '@/lib/api';

const AdminAnalytics = () => {
  // Fetch Real Analytics Data
  const { data: analytics, isLoading: isLoadingAnalytics } = useQuery({
    queryKey: ['analytics_stats'],
    queryFn: analyticsApi.getStats,
    refetchInterval: 30000 // Real-time update every 30s
  });

  // Fetch Content for Counters
  const { data: projects } = useQuery({
    queryKey: ['projects'],
    queryFn: projectsApi.getAll
  });

  const { data: services } = useQuery({
    queryKey: ['services'],
    queryFn: servicesApi.getAll
  });

  const { data: clients } = useQuery({
    queryKey: ['clients'],
    queryFn: clientsApi.getAll
  });

  const { data: testimonials } = useQuery({
    queryKey: ['testimonials'],
    queryFn: testimonialsApi.getAll
  });

  const totalProjects = projects?.length || 0;
  const featuredProjects = projects?.filter(p => p.featured).length || 0;
  const totalServices = services?.length || 0;
  const totalClients = clients?.length || 0;
  const totalTestimonials = testimonials?.length || 0;

  const topProjects = projects
    ? [...projects]
      .sort((a, b) => (b.kpis?.length || 0) - (a.kpis?.length || 0))
      .slice(0, 5)
    : [];

  const getPercentChange = (current: number, previous: number) => {
    if (previous === 0) return current > 0 ? '+100%' : '0.0%';
    const change = ((current - previous) / previous) * 100;
    return (change > 0 ? '+' : '') + change.toFixed(1) + '%';
  };

  const getTrendIcon = (current: number, previous: number) => {
    if (current >= previous) return <TrendingUp className="w-4 h-4 text-green-600" />;
    return <TrendingDown className="w-4 h-4 text-red-600" />;
  };

  const getTrendColor = (current: number, previous: number) => {
    if (current >= previous) return "text-green-600";
    return "text-red-600";
  };

  // Compute Recent Activity
  const getActivityFeed = () => {
    const allItems = [
      ...(projects?.map(p => ({ ...p, type: 'project', label: p.title })) || []),
      ...(services?.map(s => ({ ...s, type: 'service', label: s.name })) || []),
      ...(clients?.map(c => ({ ...c, type: 'client', label: c.name })) || []),
      ...(testimonials?.map(t => ({ ...t, type: 'testimonial', label: t.name })) || [])
    ];

    // Sort logic would go here if we had uniform dates. For now, we take top 5.

    return allItems.slice(0, 5).map(item => ({
      type: item.type,
      action: `${item.type === 'project' ? 'Projeto' : item.type === 'service' ? 'Serviço' : item.type === 'client' ? 'Cliente' : 'Depoimento'} "${item.label}" ativo no sistema`,
      time: 'Recentemente'
    }));
  };

  const recentActivity = getActivityFeed();

  const getActivityIcon = (type: string) => {
    switch (type) {
      case 'project': return <Target className="w-4 h-4" />;
      case 'service': return <Award className="w-4 h-4" />;
      case 'testimonial': return <MessageSquare className="w-4 h-4" />;
      case 'client': return <Users className="w-4 h-4" />;
      default: return <Activity className="w-4 h-4" />;
    }
  };

  const getActivityColor = (type: string) => {
    switch (type) {
      case 'project': return 'text-blue-600 bg-blue-50';
      case 'service': return 'text-green-600 bg-green-50';
      case 'testimonial': return 'text-purple-600 bg-purple-50';
      case 'client': return 'text-orange-600 bg-orange-50';
      default: return 'text-gray-600 bg-gray-50';
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      {/* Premium Sticky Header */}
      <div className="sticky top-0 z-20 bg-background/80 backdrop-blur-md pb-4 pt-2 border-b mb-6 -mx-6 px-6 flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Analytics</h1>
          <p className="text-muted-foreground hidden md:block">
            Visão em tempo real do desempenho do portfólio
          </p>
        </div>
        <div className="flex items-center space-x-2">
          <Badge variant="outline" className="animate-pulse bg-green-500/10 text-green-600 border-green-200">
            <Activity className="w-3 h-3 mr-1" />
            Ao vivo
          </Badge>
          <Badge variant="outline" className="hidden sm:flex">
            <Calendar className="w-3 h-3 mr-1" />
            Últimos 30 dias
          </Badge>
          <Button variant="outline" size="sm" onClick={() => window.location.reload()} className="h-8">
            <RefreshCw className="w-3.5 h-3.5 mr-2" />
            Atualizar
          </Button>
        </div>
      </div>

      {isLoadingAnalytics ? (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          {[1, 2, 3, 4].map(i => (
            <Card key={i} className="animate-pulse h-32" />
          ))}
        </div>
      ) : (
        /* Key Metrics */
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">
                    Visualizações da Página
                  </p>
                  <p className="text-2xl font-bold">{analytics?.pageViews.current.toLocaleString()}</p>
                  <div className="flex items-center space-x-1 mt-1">
                    {getTrendIcon(analytics?.pageViews.current || 0, analytics?.pageViews.previous || 0)}
                    <span className={`text-sm font-medium ${getTrendColor(analytics?.pageViews.current || 0, analytics?.pageViews.previous || 0)}`}>
                      {getPercentChange(analytics?.pageViews.current || 0, analytics?.pageViews.previous || 0)}
                    </span>
                  </div>
                </div>
                <div className="p-3 bg-blue-50 rounded-full">
                  <Eye className="w-6 h-6 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">
                    Visitantes Únicos
                  </p>
                  <p className="text-2xl font-bold">{analytics?.uniqueVisitors.current.toLocaleString()}</p>
                  <div className="flex items-center space-x-1 mt-1">
                    {getTrendIcon(analytics?.uniqueVisitors.current || 0, analytics?.uniqueVisitors.previous || 0)}
                    <span className={`text-sm font-medium ${getTrendColor(analytics?.uniqueVisitors.current || 0, analytics?.uniqueVisitors.previous || 0)}`}>
                      {getPercentChange(analytics?.uniqueVisitors.current || 0, analytics?.uniqueVisitors.previous || 0)}
                    </span>
                  </div>
                </div>
                <div className="p-3 bg-green-50 rounded-full">
                  <Users className="w-6 h-6 text-green-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">
                    Submissões de Contato
                  </p>
                  <p className="text-2xl font-bold">{analytics?.contactSubmissions.current.toLocaleString()}</p>
                  <div className="flex items-center space-x-1 mt-1">
                    {getTrendIcon(analytics?.contactSubmissions.current || 0, analytics?.contactSubmissions.previous || 0)}
                    <span className={`text-sm font-medium ${getTrendColor(analytics?.contactSubmissions.current || 0, analytics?.contactSubmissions.previous || 0)}`}>
                      {getPercentChange(analytics?.contactSubmissions.current || 0, analytics?.contactSubmissions.previous || 0)}
                    </span>
                  </div>
                </div>
                <div className="p-3 bg-purple-50 rounded-full">
                  <MousePointer className="w-6 h-6 text-purple-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">
                    Visualizações do Portfólio
                  </p>
                  <p className="text-2xl font-bold">{analytics?.portfolioViews.current.toLocaleString()}</p>
                  <div className="flex items-center space-x-1 mt-1">
                    {getTrendIcon(analytics?.portfolioViews.current || 0, analytics?.portfolioViews.previous || 0)}
                    <span className={`text-sm font-medium ${getTrendColor(analytics?.portfolioViews.current || 0, analytics?.portfolioViews.previous || 0)}`}>
                      {getPercentChange(analytics?.portfolioViews.current || 0, analytics?.portfolioViews.previous || 0)}
                    </span>
                  </div>
                </div>
                <div className="p-3 bg-orange-50 rounded-full">
                  <BarChart3 className="w-6 h-6 text-orange-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Content Overview & Recent Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Content Overview */}
        <Card>
          <CardHeader>
            <CardTitle>Visão Geral do Conteúdo</CardTitle>
            <CardDescription>
              Estatísticas dos principais elementos do portfólio
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="text-center p-4 bg-muted rounded-lg">
                <div className="text-2xl font-bold text-primary">{totalProjects}</div>
                <p className="text-sm text-muted-foreground">Projetos</p>
                <Badge variant="secondary" className="mt-1">
                  {featuredProjects} em destaque
                </Badge>
              </div>
              <div className="text-center p-4 bg-muted rounded-lg">
                <div className="text-2xl font-bold text-green-600">{totalServices}</div>
                <p className="text-sm text-muted-foreground">Serviços</p>
              </div>
              <div className="text-center p-4 bg-muted rounded-lg">
                <div className="text-2xl font-bold text-purple-600">{totalClients}</div>
                <p className="text-sm text-muted-foreground">Clientes</p>
              </div>
              <div className="text-center p-4 bg-muted rounded-lg">
                <div className="text-2xl font-bold text-orange-600">{totalTestimonials}</div>
                <p className="text-sm text-muted-foreground">Depoimentos</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Recent Activity */}
        <Card>
          <CardHeader>
            <CardTitle>Atividade Recente</CardTitle>
            <CardDescription>
              Últimas ações realizadas no painel
            </CardDescription>
          </CardHeader>
          <CardContent>
            {recentActivity.length > 0 ? (
              <div className="space-y-3">
                {recentActivity.map((activity, index) => (
                  <div key={index} className="flex items-center space-x-3 p-2 rounded-lg hover:bg-muted/50">
                    <div className={`p-2 rounded-full ${getActivityColor(activity.type)}`}>
                      {getActivityIcon(activity.type)}
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-medium">{activity.action}</p>
                      <p className="text-xs text-muted-foreground">{activity.time}</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-8 text-center text-muted-foreground">
                <Activity className="w-12 h-12 mb-4 opacity-20" />
                <p>Nenhuma atividade recente registrada.</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Top Projects Performance */}
      <Card>
        <CardHeader>
          <CardTitle>Projetos com Melhor Performance</CardTitle>
          <CardDescription>
            Projetos com mais KPIs e métricas de sucesso
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {topProjects.map((project, index) => (
              <div key={project.id} className="flex items-center justify-between p-3 rounded-lg border hover:bg-muted/50">
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center text-primary-foreground font-bold text-sm">
                    {index + 1}
                  </div>
                  <div>
                    <p className="font-medium">{project.title}</p>
                    <p className="text-sm text-muted-foreground">{project.client}</p>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <Badge variant="outline">
                    {project.kpis?.length || 0} KPIs
                  </Badge>
                  {project.featured && (
                    <Badge variant="default">
                      <Award className="w-3 h-3 mr-1" />
                      Destaque
                    </Badge>
                  )}
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

    </div>
  );
};

export default AdminAnalytics;
